// src/pages/AdminProducts.jsx
import React, { useEffect, useState } from 'react'

export default function AdminProducts() {
  const [products, setProducts]     = useState([])
  const [groups, setGroups]         = useState([])
  const [selected, setSelected]     = useState(null)
  const [form, setForm]             = useState(null)
  const [mode, setMode]             = useState('view') // view | new | edit
  const [error, setError]           = useState('')
  const [success, setSuccess]       = useState('')

  const API = process.env.REACT_APP_API_URL

  useEffect(() => {
    fetchProducts()
    fetchGroups()
  }, [])

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/products`)
      if (!res.ok) throw new Error()
      setProducts(await res.json())
    } catch {
      setError('Kunne ikke hente produkter')
    }
  }

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API}/product-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      if (!res.ok) throw new Error()
      setGroups(await res.json())
    } catch {
      console.warn('Kunne ikke hente produktgrupper')
    }
  }

  const handleNew = () => {
    setForm({
      // Basis
      name: '',
      description: '',

      // Leiepriser
      price_short_4h: '',
      price_day: '',
      price_weekend: '',

      // Kjøp
      is_for_sale: false,
      price_sale: '',
      stock_qty: '',

      // Diverse
      ProductGroupId: '',
      DiscountId: '',
      length: '',
      width: '',
      height: '',
      weight: '',
      max_weight: '',
      is_active: true,
      image: null,
      allows_rental_quantity: false,
      rental_stock_qty: ''
    })
    setSelected(null)
    setMode('new')
    setError('')
    setSuccess('')
  }

  const handleEdit = () => {
    // Sørg for at nye felter alltid finnes i form state
    const seed = {
      price_short_4h: selected?.price_short_4h ?? '',
      price_day:      selected?.price_day ?? '',
      price_weekend:  selected?.price_weekend ?? '',
      is_for_sale:    !!selected?.is_for_sale,
      price_sale:     selected?.price_sale ?? '',
      stock_qty:      selected?.stock_qty ?? '',
      allows_rental_quantity: !!selected?.allows_rental_quantity,
      rental_stock_qty: selected?.rental_stock_qty ?? ''
    }
    setForm({ ...selected, ...seed, image: null })
    setMode('edit')
    setError('')
    setSuccess('')
  }

  const handleCancel = () => {
    setForm(null)
    setMode('view')
    setError('')
    setSuccess('')
  }

  const handleChange = e => {
    const { name, value, type, checked, files } = e.target
    if (type === 'checkbox')      return setForm(prev => ({ ...prev, [name]: checked }))
    if (type === 'file')          return setForm(prev => ({ ...prev, [name]: files[0] }))
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setError(''); setSuccess('')

    try {
      const url    = `${API}/products${mode==='edit'?`/${form.id}`:''}`
      const method = mode==='edit' ? 'PUT' : 'POST'
      const fd     = new FormData()

      // Append kun felter som er satt, slik at tomme strenger ikke overskriver til 0
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== '') fd.append(k, v)
      })

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd
      })
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Feil ved lagring')

      setSuccess(mode==='edit'? 'Produkt oppdatert' : 'Nytt produkt opprettet')
      setForm(null)
      setMode('view')
      setSelected(null)
      fetchProducts()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    if (!window.confirm(`Slett produkt «${selected.name}»?`)) return

    try {
      const res = await fetch(`${API}/products/${selected.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      if (!res.ok) throw new Error('Feil ved sletting')

      setSuccess('Produkt slettet')
      setSelected(null)
      fetchProducts()
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="w-full min-h-[600px] flex gap-6">
      {/* VENSTRE: Produkt‐liste */}
      <div className="w-1/2 pr-6 border-r border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#000099]">Produkter</h2>
          <button
            onClick={handleNew}
            className="bg-[#000099] text-white px-4 py-1 rounded hover:bg-blue-800 transition"
          >
            + Ny
          </button>
        </div>
        {error && <p className="text-red-500 mb-3">{error}</p>}

        <ul className="space-y-3 overflow-y-auto max-h-[500px]">
          {products.map(p => (
            <li
              key={p.id}
              onClick={() => { setSelected(p); setMode('view'); setForm(null); setError(''); setSuccess('') }}
              className={`
                p-4 border border-gray-200 hover:bg-gray-100 rounded cursor-pointer transition
                ${selected?.id === p.id ? "border-[#000099] bg-blue-50" : "border-gray-100"}
              `}
            >
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-gray-500">
                {p.price_short_4h != null && <>&lt;4t: {p.price_short_4h} kr · </>}
                Døgn: {p.price_day ?? '-'} kr
                {p.price_weekend != null && <> · Weekend: {p.price_weekend} kr</>}
                {p.is_for_sale ? <> · Kjøp: {p.price_sale ?? '-'} kr</> : null}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* HØYRE: Detaljer eller Skjema */}
      <div className="w-1/2 pl-6">
        <h2 className="text-2xl font-bold text-[#000099] mb-4">
          {mode==='new'
            ? 'Nytt produkt'
            : mode==='edit'
            ? 'Rediger produkt'
            : 'Detaljer'}
        </h2>

        {success && <p className="text-green-600 mb-3">{success}</p>}

        {(mode==='new' || mode==='edit') && form && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Navn & Beskrivelse */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name || ''}
                onChange={handleChange}
                className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Beskrivelse
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={form.description || ''}
                onChange={handleChange}
                className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
              />
            </div>

            {/* LEIEPRISER */}
            {/* MULTIPLE LEIE */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[#000099]">Antall (utleie)</div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="allows_rental_quantity"
                  checked={!!form.allows_rental_quantity}
                  onChange={handleChange}
                />
                <span>Kan leies i antall</span>
              </label>
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">
                Antall tilgjengelig
              </label>
              <input
                type="number"
                name="rental_stock_qty"
                value={form.rental_stock_qty ?? ''}
                onChange={handleChange}
                className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                placeholder="f.eks. 50"
                disabled={!form.allows_rental_quantity}
              />
            </div>
          </div>
            <div className="border rounded-lg p-4">
              <div className="font-semibold text-[#000099] mb-2">Leiepriser</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="price_short_4h" className="block text-sm font-medium text-gray-700">
                    &lt; 4 timer (kr)
                  </label>
                  <input
                    id="price_short_4h"
                    name="price_short_4h"
                    type="number"
                    step="0.01"
                    value={form.price_short_4h ?? ''}
                    onChange={handleChange}
                    className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                    placeholder="f.eks. 650"
                  />
                </div>
                <div>
                  <label htmlFor="price_day" className="block text-sm font-medium text-gray-700">
                    Døgn (kr) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="price_day"
                    name="price_day"
                    type="number"
                    step="0.01"
                    value={form.price_day ?? ''}
                    onChange={handleChange}
                    className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                    placeholder="f.eks. 1500"
                  />
                </div>
                <div>
                  <label htmlFor="price_weekend" className="block text-sm font-medium text-gray-700">
                    Weekend (kr)
                  </label>
                  <input
                    id="price_weekend"
                    name="price_weekend"
                    type="number"
                    step="0.01"
                    value={form.price_weekend ?? ''}
                    onChange={handleChange}
                    className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                    placeholder="f.eks. 3500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Weekend = fre 16:00 → søn 22:00.
              </p>
            </div>

            {/* KJØP */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[#000099]">Kjøp (valgfritt)</div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="is_for_sale"
                    name="is_for_sale"
                    type="checkbox"
                    checked={!!form.is_for_sale}
                    onChange={handleChange}
                  />
                  <span>Produktet kan kjøpes</span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                <div>
                  <label htmlFor="price_sale" className="block text-sm font-medium text-gray-700">
                    Kjøpspris (kr)
                  </label>
                  <input
                    id="price_sale"
                    name="price_sale"
                    type="number"
                    step="0.01"
                    value={form.price_sale ?? ''}
                    onChange={handleChange}
                    className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                    placeholder="f.eks. 990"
                    disabled={!form.is_for_sale}
                  />
                </div>
                <div>
                  <label htmlFor="stock_qty" className="block text-sm font-medium text-gray-700">
                    Lager (stk)
                  </label>
                  <input
                    id="stock_qty"
                    name="stock_qty"
                    type="number"
                    step="1"
                    value={form.stock_qty ?? ''}
                    onChange={handleChange}
                    className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                    placeholder="f.eks. 10"
                    disabled={!form.is_for_sale}
                  />
                </div>
              </div>
            </div>

            {/* Gruppe & Bilde */}
            <div>
              <label htmlFor="ProductGroupId" className="block text-sm font-medium text-gray-700">
                Produktgruppe
              </label>
              <select
                id="ProductGroupId"
                name="ProductGroupId"
                value={form.ProductGroupId || ''}
                onChange={handleChange}
                className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
              >
                <option value="">— Velg gruppe —</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="image" className="block text-sm font-medium text-gray-700">
                Bilde
              </label>
              <input
                id="image"
                name="image"
                type="file"
                accept="image/*"
                onChange={handleChange}
                className="mt-1"
              />
            </div>
            {mode==='edit' && selected?.image_url && (
              <img src={selected.image_url} alt="" className="mt-2 h-24 object-cover rounded" />
            )}

            {/* Tekniske felter (valgfritt) */}
            <div className="grid grid-cols-2 gap-4">
              {['length','width','height','weight','max_weight'].map(field => (
                <div key={field}>
                  <label htmlFor={field} className="block text-sm font-medium text-gray-700">
                    {field.charAt(0).toUpperCase() + field.slice(1)} (valgfritt)
                  </label>
                  <input
                    id={field}
                    name={field}
                    type="number"
                    step="0.01"
                    value={form[field] ?? ''}
                    onChange={handleChange}
                    className="mt-1 w-full border px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                  />
                </div>
              ))}
            </div>

            {/* Aktiv checkbox */}
            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={!!form.is_active}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Aktiv
              </label>
            </div>

            {/* Lagre / Avbryt */}
            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
              >
                Lagre
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-300 px-6 py-2 rounded hover:bg-gray-400 transition"
              >
                Avbryt
              </button>
            </div>
          </form>
        )}

        {/* Detaljvisning */}
        {mode==='view' && selected && (
          <div className="space-y-3 text-gray-800">
            <p><strong>Navn:</strong> {selected.name}</p>
            <p><strong>Beskrivelse:</strong> {selected.description || '-'}</p>

            <div className="border rounded p-3">
              <div className="font-medium mb-1">Leiepriser</div>
              <p>
                {selected.price_short_4h != null && <> &lt;4t: {selected.price_short_4h} kr · </>}
                Døgn: {selected.price_day ?? '-'} kr
                {selected.price_weekend != null && <> · Weekend: {selected.price_weekend} kr</>}
              </p>
            </div>
            <div className="border rounded p-3">
            <div className="font-medium mb-1">Antall (utleie)</div>
            <p>
              {selected.allows_rental_quantity
                ? <>Ja – {selected.rental_stock_qty ?? '-'} stk tilgjengelig</>
                : 'Nei (kun 1 stk per booking)'}
            </p>
          </div>
            <div className="border rounded p-3">
              <div className="font-medium mb-1">Kjøp</div>
              <p>
                {selected.is_for_sale
                  ? <>Ja – {selected.price_sale ?? '-'} kr{selected.stock_qty != null ? ` · Lager: ${selected.stock_qty}` : ''}</>
                  : 'Nei'}
              </p>
            </div>

            <p><strong>Gruppe:</strong> {groups.find(g => g.id === selected.ProductGroupId)?.name || '-'}</p>
            {selected.image_url && (
              <img src={selected.image_url} alt="" className="h-32 object-cover rounded" />
            )}

            <div className="flex gap-4 pt-4">
              <button
                onClick={handleEdit}
                className="bg-yellow-500 text-white px-6 py-2 rounded hover:bg-yellow-600 transition"
              >
                Rediger
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition"
              >
                Slett
              </button>
            </div>
          </div>
        )}

        {mode==='view' && !selected && (
          <p className="text-gray-600">Velg et produkt for detaljer.</p>
        )}
      </div>
    </div>
  )
}
